import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useTheme } from "../../../hooks/useTheme.js";

// Confirmation dialog for the pre-interview screens.
//
// The shared MUI ConfirmationDialog is deliberately not reused here: it forces
// Poppins and MUI's own spacing scale, which reads as a different product next
// to these steps. This one takes its palette, radii and pill controls from
// StepHeader and ConsentStep so it belongs to the page it interrupts, but runs
// a step down from them on type and padding on purpose — this is a two-line
// interruption, not a page, and at the step's own sizes it dominated the screen
// behind it.
//
// Rendered through a portal on purpose. `.interview-shell` sets `zoom` and
// `overflow: hidden`, and a fixed child inside a zoomed ancestor is positioned
// against that ancestor rather than the viewport, so an inline dialog would sit
// off-centre on short screens where the zoom kicks in.
const InstructionsDialog = ({
  open,
  onClose,
  title,
  message,
  children,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  // "danger" for destructive confirms, which take the red the step's own
  // Decline button already uses; "primary" takes the gradient of Continue.
  tone = "primary",
  busy = false,
  showClose = true,
}) => {
  const { theme } = useTheme();
  const isLight = theme === "light";

  const panelRef = useRef(null);
  const cancelRef = useRef(null);
  // Where focus came from, so closing hands it back to the button that opened us.
  const restoreFocusRef = useRef(null);

  const handleClose = useCallback(() => {
    if (busy) return;
    onClose?.();
  }, [busy, onClose]);

  useEffect(() => {
    if (!open) return undefined;

    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Cancel takes focus rather than the confirm button: on a destructive
    // dialog a stray Enter should not be the thing that confirms it.
    const focusTimer = window.setTimeout(() => {
      (cancelRef.current || panelRef.current)?.focus();
    }, 0);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        handleClose();
        return;
      }

      if (event.key !== "Tab") return;

      // Keep Tab inside the dialog while it is up.
      const focusables = panelRef.current?.querySelectorAll(
        'button:not([disabled]), a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables || focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown, true);
      restoreFocusRef.current?.focus?.();
    };
  }, [open, handleClose]);

  if (!open) return null;

  const confirmClasses =
    tone === "danger"
      ? "bg-red-600 hover:bg-red-700 text-white"
      : "bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90 text-white";

  return createPortal(
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 ${
        isLight ? "bg-slate-900/20" : "bg-black/50"
      }`}
      style={{ backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
      onMouseDown={(event) => {
        // mousedown, not click: a drag that starts inside the panel and ends on
        // the backdrop should not count as dismissing it.
        if (event.target === event.currentTarget) handleClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "instructions-dialog-title" : undefined}
        aria-describedby={message ? "instructions-dialog-message" : undefined}
        tabIndex={-1}
        className={`relative w-full max-w-[352px] rounded-xl border p-5 outline-none ${
          isLight
            ? "bg-white border-[#ECEDF5] shadow-[0_10px_34px_rgba(16,24,40,0.12)]"
            : "bg-gray-800/95 border-gray-700/60 backdrop-blur-sm shadow-[0_10px_34px_rgba(0,0,0,0.45)]"
        }`}
      >
        {showClose && (
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className={`absolute top-3.5 right-3.5 rounded-full p-1 transition-colors ${
              isLight
                ? "text-slate-400 hover:text-slate-600 hover:bg-[#F6F7FB]"
                : "text-gray-500 hover:text-gray-300 hover:bg-gray-700/50"
            }`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}

        {title && (
          <h2
            id="instructions-dialog-title"
            className={`text-left text-[14px] font-semibold leading-snug pr-6 ${
              isLight ? "text-slate-900" : "text-white"
            }`}
          >
            {title}
          </h2>
        )}

        {message && (
          <p
            id="instructions-dialog-message"
            className={`mt-1.5 text-[12.5px] leading-relaxed ${
              isLight ? "text-slate-500" : "text-gray-400"
            }`}
          >
            {message}
          </p>
        )}

        {children}

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            ref={cancelRef}
            type="button"
            onClick={handleClose}
            disabled={busy}
            className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isLight
                ? "border-[#E1E3EC] text-slate-600 hover:bg-[#F6F7FB]"
                : "border-gray-600 text-gray-300 hover:bg-gray-700/40"
            }`}
          >
            {cancelText}
          </button>

          <button
            type="button"
            onClick={() => onConfirm?.()}
            disabled={busy}
            className={`px-4 py-1.5 rounded-full text-[12.5px] font-medium transition-opacity disabled:opacity-60 disabled:cursor-not-allowed ${confirmClasses}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default InstructionsDialog;
